import React, { useMemo, useCallback } from 'react';
import { Card, Table, Spin } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from '@ant-design/icons';
import './StatisticsTable.scss';

/**
 * Extracts a string key from an item's property value
 * Handles objects, strings, numbers, and null/undefined values
 */
const extractKey = (value) => {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value.name || value.display_name || value.id || JSON.stringify(value);
  }
  return String(value) || 'N/A';
};

const StatisticsTable = ({ 
  title, 
  currentPeriodData = [], 
  previousPeriodData = [], 
  loading = false,
  groupByKey = 'name',
  groupByLabel = 'Nome',
  countKey = 'count'
}) => {
  const getLatestDateData = useCallback((data) => {
    if (!data || data.length === 0) return {};

    const latestDate = data.reduce((max, item) => {
      const itemDate = item.date || '';
      return itemDate > max ? itemDate : max;
    }, '');

    const latestItems = data.filter(item => item.date === latestDate);

    const result = {};
    latestItems.forEach(item => {
      const key = extractKey(item[groupByKey]);
      result[key] = {
        key: key,
        label: key,
        total: item[countKey] || 0,
        rawData: item[groupByKey]
      };
    });
    return result;
  }, [groupByKey, countKey]);

  const tableData = useMemo(() => {
    const currentAggregated = getLatestDateData(currentPeriodData);
    const previousAggregated = getLatestDateData(previousPeriodData);
    
    // Merge both periods
    const allKeys = new Set([
      ...Object.keys(currentAggregated),
      ...Object.keys(previousAggregated)
    ]);
    
    return Array.from(allKeys).map(key => {
      const current = currentAggregated[key]?.total || 0;
      const previous = previousAggregated[key]?.total || 0;
      const variation = previous > 0 
        ? ((current - previous) / previous * 100)
        : current > 0 ? 100 : 0;
      
      return {
        key: key,
        name: currentAggregated[key]?.label || previousAggregated[key]?.label || key,
        currentPeriod: current,
        previousPeriod: previous,
        variation: variation,
        rawData: currentAggregated[key]?.rawData || previousAggregated[key]?.rawData
      };
    }).sort((a, b) => b.currentPeriod - a.currentPeriod); // Sort by current period descending
  }, [currentPeriodData, previousPeriodData, getLatestDateData]);

  const getVariationColor = (value) => {
    if (value > 0) return '#52c41a';
    if (value < 0) return '#ff4d4f';
    return '#8c8c8c';
  };

  const getVariationIcon = (value) => {
    if (value > 0) return <ArrowUpOutlined />;
    if (value < 0) return <ArrowDownOutlined />;
    return <MinusOutlined />;
  };

  const formatNumber = (value) => {
    return value.toLocaleString('pt-BR');
  };

  const columns = [
    {
      title: groupByLabel,
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 200,
    },
    {
      title: 'Período Atual',
      dataIndex: 'currentPeriod',
      key: 'currentPeriod',
      render: (value) => formatNumber(value),
      align: 'right',
      width: '20%',
      sorter: (a, b) => a.currentPeriod - b.currentPeriod,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Período Anterior',
      dataIndex: 'previousPeriod',
      key: 'previousPeriod',
      render: (value) => formatNumber(value),
      align: 'right',
      width: '20%',
      sorter: (a, b) => a.previousPeriod - b.previousPeriod,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Variação',
      dataIndex: 'variation',
      key: 'variation',
      render: (value) => (
        <span style={{ color: getVariationColor(value) }}>
          {getVariationIcon(value)} {Math.abs(value).toFixed(2)}%
        </span>
      ),
      align: 'right',
      width: '20%',
      sorter: (a, b) => a.variation - b.variation,
      sortDirections: ['descend', 'ascend'],
    },
  ];

  return (
    <Card 
      title={title}
      className="statistics-table-card"
      size="small"
    >
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={tableData}
          size="small"
          pagination={{
            size: 'small',
            showSizeChanger: true,
            showQuickJumper: false,
            pageSize: 10,
            pageSizeOptions: ['10', '20', '50'],
          }}
          scroll={false}
        />
      </Spin>
    </Card>
  );
};

export default StatisticsTable;
